import { Alert, AlertTitle } from '@mui/material'
import { format } from 'date-fns'
import type { FC } from 'react'
import { useContext, useState } from 'react'
import styled from 'styled-components'

import { Button, AlternateButton } from '@/components/DEPRECATED_UI/Button'
import SlideInSidebar from '@/components/DEPRECATED_UI/SlideInSidebar'
import H2 from '@/components/H2'
import TextInput from '@/components/input/TextInput'
import LoadingSpinner from '@/components/LoadingSpinner'
import ctx from '@/context/AppContext'
import { useVerifyOrganisationCreditMutation } from '@/lib/apollo/mutation/verify-organisation-credit'
import type { ErrorContext } from '@/lib/apollo/utils/getUpstreamErrors'
import { getErrorsFromGraphQLExtensions } from '@/lib/apollo/utils/getUpstreamErrors'
import type { CompanyCCJ, CompanyCredit } from '@/types/Activity'

import OrganisationCCJsFields from './OrganisationCCJsFields'

const ContentContainer = styled.div`
  display: grid;
  gap: 20px;
  margin-top: 20px;
`

export interface Props {
  customerId: string
  subjectId: string
  credit?: CompanyCredit
  onClose: () => void
}

interface Fields {
  creditScore: number
  creditLimit: number
  ccjs: CompanyCCJ[]
}

const defaultCCJ: CompanyCCJ = {
  amount: 0,
  courtDate: format(new Date(), 'yyyy-MM-dd'),
  state: 'Active',
}

const VerifyOrganisationCreditSidebar: FC<Props> = ({
  customerId,
  subjectId,
  credit,
  onClose,
}) => {
  const [errors, setErrors] = useState<ErrorContext<string>[] | undefined>()
  const [fields, setFields] = useState<Fields>({
    creditScore: credit?.creditScore ?? 0,
    creditLimit: credit?.creditLimit ?? 0,
    ccjs: credit?.ccJs ?? [],
  })

  const [verifyOrganisationCredit, { loading, error, data }] =
    useVerifyOrganisationCreditMutation()

  const { personId } = useContext(ctx)

  const onConfirm = () => {
    verifyOrganisationCredit({
      variables: {
        customerId,
        subjectId,
        userId: personId ?? '',
        request: {
          creditScore: fields.creditScore,
          creditLimit: fields.creditLimit,
          ccjs: fields.ccjs,
        },
      },
      onError: error => {
        setErrors(getErrorsFromGraphQLExtensions(error))
      },
    })
  }

  return (
    <SlideInSidebar
      onClose={onClose}
      header={<H2>Verify organisation credit</H2>}
      content={
        <ContentContainer>
          {data !== undefined || loading ? (
            <LoadingSpinner />
          ) : (
            <div>
              <TextInput
                label="Credit Score"
                onChange={e => setFields({ ...fields, creditScore: +e.target.value })}
                type="number"
                value={fields.creditScore}
                required
              />
              <TextInput
                label="Credit Limit"
                onChange={e => setFields({ ...fields, creditLimit: +e.target.value })}
                type="number"
                value={fields.creditLimit}
                required
              />
              <OrganisationCCJsFields
                onAdd={() => setFields({ ...fields, ccjs: [...fields.ccjs, defaultCCJ] })}
                values={fields.ccjs}
                onChangeValues={ccjs => setFields({ ...fields, ccjs })}
              />
              {errors && (
                <Alert severity="error">
                  <AlertTitle>
                    There was an error submitting the information provided.
                  </AlertTitle>
                  {errors !== undefined && errors.length > 0 ? (
                    <ol>
                      {errors.map(({ description, reason }, i) => (
                        <li key={i}>
                          {description} - ({reason})
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <span>Please try again</span>
                  )}
                </Alert>
              )}
            </div>
          )}
        </ContentContainer>
      }
      footer={
        <>
          {error && <div>An unexpected error occurred. {error?.message}</div>}
          <div className="side-by-side-flex-end">
            <AlternateButton onClick={onClose}>Cancel</AlternateButton>
            <Button disabled={loading} onClick={onConfirm}>
              Confirm
            </Button>
          </div>
        </>
      }
    />
  )
}

export default VerifyOrganisationCreditSidebar
